import React, { useState, useEffect } from 'react';
import Breadcrumb from '../components/layout/Breadcrumb';
import Card from '../components/ui/Card';
import { Users, Package, MapPin, Phone, Mail, Calendar, Eye, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import api from '../services/api';

const Customer = () => {
  const [centers, setCenters] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      console.log('Fetching centers...');
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      
      // Fetch centers with proper authentication
      const response = await api.get('/api/newCenters', {
        params: {
          access_token: token,
          filter: JSON.stringify({
            include: [
              'newWasteCollecteds',
              'newCustomerLists', 
              'aggregatedWastes',
              'customers',
              'soldWastes',
              'trips',
              'raisecomplainLists'
            ]
          })
        }
      });
      
      console.log('Full API response:', response);
      
      // Handle different possible response formats
      let processedData;
      if (Array.isArray(response)) {
        processedData = response;
      } else if (response && typeof response === 'object' && Array.isArray(response.data)) {
        processedData = response.data;
      } else if (response && typeof response === 'object' && response.length !== undefined) {
        // If it's an object with a length property, treat as array-like
        processedData = Array.from(response);
      } else {
        processedData = [];
      }
      
      console.log('Processed centers data:', processedData);
      setCenters(processedData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching centers:', err);
      setError('Failed to load centers: ' + (err.response?.data?.error?.message || err.message || 'Unknown error'));
      setCenters([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCustomersForCenter = async (centerId) => {
    try {
      setCustomersLoading(true);
      console.log('Fetching customers for center:', centerId);
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token');
      
      // Fetch all customers with proper authentication
      const response = await api.get('/api/customers', {
        params: {
          access_token: token,
          filter: JSON.stringify({
            include: ['moneycollections', 'individualCollections', 'coordinates']
          })
        }
      });
      
      console.log('All customers received:', response);
      
      // Handle different possible response formats for customers
      let processedCustomers;
      if (Array.isArray(response)) {
        processedCustomers = response;
      } else if (response && typeof response === 'object' && Array.isArray(response.data)) {
        processedCustomers = response.data;
      } else if (response && typeof response === 'object' && response.length !== undefined) {
        processedCustomers = Array.from(response);
      } else {
        processedCustomers = [];
      }
      
      const centerCustomers = processedCustomers.filter(customer => {
        // Check different possible field names for center association
        return customer.newCenterId === centerId || 
               customer.centerId === centerId || 
               customer.center === centerId;
      });
      
      console.log('Customers for center:', centerCustomers);
      setCustomers(centerCustomers);
      setError(null);
    } catch (err) {
      console.error('Error fetching customers for center:', err);
      setError('Failed to load customers for selected center: ' + (err.response?.data?.error?.message || err.message || 'Unknown error'));
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };
  
  // Handle center selection
  const handleCenterChange = (e) => {
    const centerId = e.target.value;
    setSelectedCenter(centerId);
    
    if (centerId) {
      fetchCustomersForCenter(centerId);
    } else {
      setCustomers([]);
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb />

      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            View centers and their associated customers
          </p>
        </div>
      </div>

      {/* Center Selection Dropdown */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-gray-600 mr-2" />
            <label className="text-sm font-medium text-gray-700 mr-3">Select Center:</label>
            <select 
              value={selectedCenter}
              onChange={handleCenterChange}
              className="input border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select a Center --</option>
              {centers.map((center) => (
                <option key={center.id} value={center.id || center._id}>
                  {center.centerName || center.name || 'Unnamed Center'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Selected Center</p>
              <p className="text-2xl font-bold text-gray-900">
                {selectedCenter ? centers.find(c => c.id === selectedCenter)?.centerName || 'Selected Center' : 'None'}
              </p>
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100">
              <Users className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Customers in Selected Center</p>
              <p className="text-2xl font-bold text-gray-900">
                {customers.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Customers List for Selected Center */}
      <Card>
        {selectedCenter && customersLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading customers for selected center...</p>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-red-500">{error}</p>
          </div>
        ) : !selectedCenter ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">Please select a center to view its customers</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">No customers found for the selected center</p>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Customers in {centers.find(c => c.id === selectedCenter)?.centerName || 'Selected Center'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <div key={customer.id || customer._id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900 flex items-center">
                        <Users className="h-4 w-4 mr-2 text-gray-600" />
                        {customer.name || customer.firstName || customer.lastname || 'Unknown Customer'}
                      </h5>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {customer.email || customer.contactEmail || 'No email'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <Phone className="h-3 w-3 mr-1" />
                        {customer.phone || customer.mobile || customer.contact || 'No phone'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {customer.address || customer.location || 'Address not specified'}
                      </p>
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Registered: {formatDate(customer.createdAt || customer.createdOn)}
                      </p>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600 p-1">
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Customer Stats */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Collections:</span>
                      <span className="font-medium">
                        {customer.individualCollections?.length || customer.moneycollections?.length || 0}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Last Visit:</span>
                      <span className="font-medium">
                        {formatDate(
                          customer.individualCollections?.slice(-1)[0]?.date || 
                          customer.moneycollections?.slice(-1)[0]?.date || 
                          customer.updatedAt
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Customer;